import { atom } from 'infrastructure/state/jotai.wrapper'
import { googleUserAtom, getSpreadsheet } from 'infrastructure/googleDrive'
import { INode } from 'shared/types/tree'
import { StandardsMapping, TableData } from './types'
import { createIdMap } from '../../version_mapping/utility'
import { getStandardsMapFromNames } from 'components/tree/standardUtilities'
import { NodeBasedUpdates } from '../nodeUpdates/types'
import {
  convertSheetDataToMappedSkills,
  getQuestionNodeForSeries,
  getUpdatesForQuestionNode,
} from './atomUtility'

export const updatingAtom = atom(false)
export const standardStateAtom = atom('')
export const sheetIdAtom = atom('1vV5v44dnM3zrs21H4dZaXatjCRvL0xjyyGq3WlNPfyQ')
export const treeAtom = atom<{ tree?: INode; resetTree?: () => void }>({})
export const treeIdMapAtom = atom((get) => {
  const { tree } = get(treeAtom)
  if (!tree) return
  return createIdMap(tree)
})

export const parsedSheetDataAtom = atom(async (get) => {
  const guser = get(googleUserAtom)
  const sheetId = get(sheetIdAtom)

  if (!sheetId || !guser.credential?.accessToken) return {}

  const data = await getSpreadsheet(guser.credential.accessToken, sheetId)
  if (data.error) {
    return {
      data: undefined,
      title: `Error Getting Sheet: ${data.error.status} - ${data.error.message}`,
    }
  }
  const title = data.properties.title
  return { title, data: convertSheetDataToMappedSkills(data) }
})

export const mappedUpdatesData = atom(async (get) => {
  const { tree } = get(treeAtom)
  const { data } = await get(parsedSheetDataAtom)
  if (!tree || !data) return (data ?? []) as TableData[]
  return data.map<TableData>((series) => {
    const { seriesId } = series
    return {
      ...series,
      questionNode: getQuestionNodeForSeries(seriesId, tree),
    }
  })
})

export const allSkillsInSheetAtom = atom(async (get) => {
  const { data } = await get(parsedSheetDataAtom)
  if (!data) return
  const allSkillNames = data.reduce<string[]>((acc, { skillNames }) => {
    return [...acc, ...skillNames]
  }, [])
  return Array.from(new Set(allSkillNames))
})

export const standardsMapAtom = atom(async (get) => {
  const skillNames = await get(allSkillsInSheetAtom)
  const state = get(standardStateAtom)
  if (!skillNames) return { standardsMap: undefined }
  return getStandardsMapFromNames(skillNames, { state })
})

export const allUpdatesAtom = atom(async (get) => {
  const { standardsMap } = await get(standardsMapAtom)
  const sheetData = await get(mappedUpdatesData)
  if (!standardsMap) return
  return sheetData.reduce<NodeBasedUpdates[]>(
    (acc, { skillNames, questionNode }) => {
      if (!questionNode) return acc
      const skillIds = skillNames.map((name) => standardsMap[name]?.standardId)
      return [...acc, ...getUpdatesForQuestionNode(skillIds, questionNode)]
    },
    [],
  )
})

export const mappedStandardsData = atom(async (get) => {
  const allSkills = await get(allSkillsInSheetAtom)
  const { standardsMap } = await get(standardsMapAtom)
  if (!allSkills || !standardsMap) return
  return allSkills.map<StandardsMapping>((sheetId) => {
    return {
      sheetId,
      mappedTo: standardsMap[sheetId]?.standardName,
      mappedId: standardsMap[sheetId]?.standardId,
    }
  })
})
